// apiWrapper.js
import { API_BASE_URL } from "./api-config.js";
import { getAuth, signOut } from "firebase/auth";
import { getCachedUser } from "./authUser";

const auth = getAuth(); // Firebase Authentication インスタンス

// トークンの自動更新（グローバルキャッシュから取得）
export const refreshAuthToken = async () => {
  const user = getCachedUser();
  if (!user) {
    console.warn("ユーザーが未ログイン状態です");
    return null;
  }
  try {
    return await user.getIdToken(true);
  } catch (error) {
    console.error("トークン更新エラー:", error);
    return null;
  }
};


// ユーザーを強制ログアウト
// ユーザーを強制ログアウトし、指定したページへリダイレクト
const logoutUser = async (redirectUrl) => {
  console.warn("ログアウト処理を実行します...");

  let userRole = "";
  try {
    userRole = sessionStorage.getItem("userRole") || "";
  } catch (err) {
    console.error("sessionStorage.getItem error:", err);
  }

  console.log("Before signOut, currentUser:", auth.currentUser);
  console.log("Before signOut, userRole:", userRole);

  try {
    await signOut(auth);
    console.log("SignOut completed successfully!");
  } catch (err) {
    console.error("SignOut error:", err);
  }

  sessionStorage.clear();

  if (userRole === "") {
    return; // ログアウト済みの可能性
  }

  console.warn("Redirecting to:", redirectUrl);
  window.location.replace(redirectUrl);
};

const waitForToken = async (timeoutMs = 3000) => {
  const start = Date.now();
  while (!auth.currentUser) {
    if (Date.now() - start > timeoutMs) break;
    await new Promise((res) => setTimeout(res, 100)); // 100ms ごとに確認
  }

  if (!auth.currentUser) return null;

  try {
    return await auth.currentUser.getIdToken();
  } catch (error) {
    console.error("トークン取得失敗:", error);
    return null;
  }
};

// ヘッダーを作成
const getAuthHeaders = async (customHeaders = {}) => {
  let token = sessionStorage.getItem("jwtToken");

  if (!token) {
    console.warn("sessionStorage に jwtToken が見つかりません、待機を開始します...");
    token = await waitForToken();

    if (token) {
      sessionStorage.setItem("jwtToken", token);
    } else {
      console.error("トークンが取得できませんでした（タイムアウト）");
    }
  }

  return {
    ...customHeaders,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

// APIリクエストを自動リトライ（401ならトークン更新して再試行）
const fetchWithAuth = async (url, options = {}) => {
  try {
    const headers = await getAuthHeaders(options.headers);
    let response = await fetch(url, { ...options, headers });

    if (response.status === 401) {
      console.log("JWTの期限切れ。リフレッシュを試行します...");
      const newToken = await refreshAuthToken();

      if (newToken) {
        console.log("トークンを更新しました");
        sessionStorage.setItem("jwtToken", newToken);
        options.headers = { ...options.headers, Authorization: `Bearer ${newToken}` };
        response = await fetch(url, options);
      } else {
        console.warn("トークン更新に失敗。ログアウト & ログインページへ遷移。");
        await logoutUser("/logout");
        return null;
      }
    }

    if (response.status == 400) {
      console.error("Bad request エラーページへ遷移。");
      window.location.replace("/error/400");
      return null;
    } else if (response.status >= 500) {
      console.error("サーバーエラー発生。エラーページへ遷移。");
      window.location.replace("/error/500");
      return null;
    }

    return response;
  } catch (error) {
    console.error("APIリクエストエラー:", error);

    // ネットワークエラー時もエラーページへ
    window.location.replace("/error/500");
    return null;
  }
};


/**
 * GETリクエスト用ラッパー
 */
export const getRequest = async (endpoint, options = {}) => {
  const url = new URL(`${API_BASE_URL}/${endpoint}`);
  const response = await fetchWithAuth(url.toString(), { ...options, method: "GET" });

  if (!response) return null; // トークン更新失敗時にリクエストを中断
  if (!response.ok) {
    throw new Error(`GET request failed: ${response.statusText}`);
  }
  return await response.json();
};

/**
 * GETリクエスト用ラッパー（raw response）
 */
export const getRequestRow = async (endpoint, options = {}) => {
  const url = new URL(`${API_BASE_URL}/${endpoint}`);
  return await fetchWithAuth(url.toString(), { ...options, method: "GET" });
};

/**
 * POSTリクエスト用ラッパー
 */
export const postRequest = async (endpoint, body = {}, options = {}) => {
  const finalBody = JSON.stringify(body);
  console.log("final body:", finalBody);
  return await fetchWithAuth(`${API_BASE_URL}/${endpoint}`, {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options.headers },
    body: finalBody,
  });
};

/**
 * PUTリクエスト用ラッパー
 */
export const putRequest = async (endpoint, body = {}, options = {}) => {
  const finalBody = JSON.stringify(body);
  return await fetchWithAuth(`${API_BASE_URL}/${endpoint}`, {
    ...options,
    method: "PUT",
    headers: { "Content-Type": "application/json", ...options.headers },
    body: finalBody,
  });
};

/**
 * DELETEリクエスト用ラッパー
 */
export const deleteRequest = async (endpoint, body = {}, options = {}) => {
  const finalBody = JSON.stringify(body);
  return await fetchWithAuth(`${API_BASE_URL}/${endpoint}`, {
    ...options,
    method: "DELETE",
    headers: { "Content-Type": "application/json", ...options.headers },
    body: finalBody,
  });
};

// ファイル送信用専用 POST ラッパー
export const postFile = async (endpoint, formData, options = {}) => {
  return await fetchWithAuth(`${API_BASE_URL}/${endpoint}`, {
    ...options,
    method: "POST",
    body: formData, // Content-Type は自動付加されるので headers は不要
  });
};
